import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Plus, Star, Search } from 'lucide-react';

import { api } from '../lib/api.js';
import { useApiData } from '../lib/hooks.js';
import { useToast } from '../lib/store.js';
import { num, SLOT_LABEL } from '../lib/format.js';
import { Card, Button, Sheet, Field, TextInput, NumberInput, TextArea, Choice, Spinner, ErrorNote, Pill, PageHeader, EmptyState } from '../components/ui.jsx';

// The meal bank. Everything in it can be swapped into any week, and she can
// add her own — which is the half of the hybrid approach the brief asked for.

const SLOTS = ['breakfast', 'lunch', 'dinner', 'snack'];

export default function MealBank() {
  const navigate = useNavigate();
  const [slot, setSlot] = useState('dinner');
  const [q, setQ] = useState('');
  const { data, loading, error, reload } = useApiData(`/nutrition/meals?slot=${slot}`, [slot]);
  const [editing, setEditing] = useState(null);

  const meals = (data?.meals || []).filter((m) => !q || `${m.name} ${m.tags || ''} ${m.ingredients || ''}`.toLowerCase().includes(q.toLowerCase()));

  return (
    <div className="max-w-lg mx-auto">
      <PageHeader
        title="Meal bank"
        subtitle="Swap any of these into the week, or add your own."
        back={() => navigate('/food')}
        right={
          <button onClick={() => setEditing({})} className="p-2 text-accent" aria-label="Add a meal">
            <Plus size={22} />
          </button>
        }
      />

      <div className="px-gutter">
        <div className="relative mb-3">
          <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-textMute" />
          <TextInput className="field pl-9" placeholder="Search" value={q} onChange={(e) => setQ(e.target.value)} />
        </div>

        <div className="mb-4">
          <Choice columns={4} value={slot} onChange={setSlot} options={SLOTS.map((s) => ({ value: s, label: SLOT_LABEL[s] }))} />
        </div>

        {slot === 'dinner' && (
          <p className="text-meta text-textMute mb-3">
            Dinners marked <em>shared</em> meet every household restriction: gluten-free, lactose-free, and free of lamb, pork, peppers,
            cucumber and coconut.
          </p>
        )}

        {loading && !data && <Spinner />}
        {error && <ErrorNote error={error} onRetry={reload} />}

        {data && meals.length === 0 && (
          <Card>
            <EmptyState title="Nothing here yet" body="Add a meal and it will be available to swap into any week." action={<Button onClick={() => setEditing({ slot })}>Add a meal</Button>} />
          </Card>
        )}

        <ul className="space-y-2 mb-8">
          {meals.map((m) => (
            <li key={m.id}>
              <button onClick={() => setEditing(m)} className="w-full text-left card hover:border-edgeHi transition-colors">
                <div className="flex items-start gap-2">
                  <div className="flex-1 min-w-0">
                    <p className="text-body font-semibold">{m.name}</p>
                    <p className="text-meta text-textMute mt-0.5">
                      {[m.kcal ? `${num(m.kcal)} kcal` : null, m.protein_g ? `${m.protein_g}g protein` : null, m.prep_min ? `${m.prep_min} min` : null]
                        .filter(Boolean)
                        .join(' · ') || 'No numbers recorded'}
                    </p>
                  </div>
                  {m.favourite ? <Star size={16} className="text-accent shrink-0 mt-0.5" fill="currentColor" /> : null}
                </div>
                <div className="flex flex-wrap gap-1.5 mt-2.5">
                  {m.shared_safe ? <Pill tone="good">Shared</Pill> : null}
                  {m.gluten_free ? <Pill tone="mute">GF</Pill> : null}
                  {m.lactose_free ? <Pill tone="mute">LF</Pill> : null}
                  {!m.user_id ? null : <Pill tone="accent">Yours</Pill>}
                </div>
              </button>
            </li>
          ))}
        </ul>
      </div>

      {editing && <MealSheet meal={editing} onClose={() => setEditing(null)} onSaved={() => { setEditing(null); reload({ quiet: true }); }} />}
    </div>
  );
}

function MealSheet({ meal, onClose, onSaved }) {
  const { show } = useToast();
  const isNew = !meal.id;
  const [form, setForm] = useState({
    slot: meal.slot || 'dinner',
    name: meal.name || '',
    kcal: meal.kcal ?? '',
    protein_g: meal.protein_g ?? '',
    serves: meal.serves ?? 2,
    prep_min: meal.prep_min ?? '',
    ingredients: meal.ingredients || '',
    method: meal.method || '',
    tags: meal.tags || '',
    notes: meal.notes || '',
    gluten_free: !!meal.gluten_free,
    lactose_free: !!meal.lactose_free,
    shared_safe: !!meal.shared_safe,
    favourite: !!meal.favourite,
  });
  const [busy, setBusy] = useState(false);
  const set = (patch) => setForm((f) => ({ ...f, ...patch }));

  async function save() {
    if (!form.name.trim()) return show('Give it a name.', 'crit');
    setBusy(true);
    try {
      if (isNew) await api.post('/nutrition/meals', form);
      else await api.put('/nutrition/meals', { id: meal.id, ...form });
      show(isNew ? 'Added to your bank.' : 'Saved.');
      onSaved();
    } catch (err) {
      show(err.message, 'crit');
    } finally {
      setBusy(false);
    }
  }

  async function archive() {
    setBusy(true);
    try {
      await api.del(`/nutrition/meals?id=${meal.id}`);
      show('Removed from the bank.');
      onSaved();
    } catch (err) {
      show(err.message, 'crit');
    } finally {
      setBusy(false);
    }
  }

  return (
    <Sheet
      open
      onClose={onClose}
      title={isNew ? 'Add a meal' : meal.name}
      footer={
        <div className="flex gap-2">
          <Button className="flex-1" onClick={save} disabled={busy}>
            {busy ? 'Saving…' : 'Save'}
          </Button>
          {!isNew && meal.user_id && (
            <Button variant="secondary" onClick={archive} disabled={busy}>
              Remove
            </Button>
          )}
        </div>
      }
    >
      {!isNew && !meal.user_id && (
        <p className="text-meta text-textMute mb-4 card-flat">
          This is one of the meals that came with the app. Saving changes makes your own copy and leaves the original alone.
        </p>
      )}

      <Field label="Name" id="mname">
        <TextInput id="mname" value={form.name} onChange={(e) => set({ name: e.target.value })} />
      </Field>

      <Field label="When" id="mslot">
        <Choice columns={4} value={form.slot} onChange={(v) => set({ slot: v })} options={SLOTS.map((s) => ({ value: s, label: SLOT_LABEL[s] }))} />
      </Field>

      <div className="grid grid-cols-3 gap-3">
        <Field label="Calories" id="mk"><NumberInput id="mk" value={form.kcal} onChange={(e) => set({ kcal: e.target.value })} /></Field>
        <Field label="Protein" id="mp"><NumberInput id="mp" suffix="g" value={form.protein_g} onChange={(e) => set({ protein_g: e.target.value })} /></Field>
        <Field label="Serves" id="ms"><NumberInput id="ms" value={form.serves} onChange={(e) => set({ serves: e.target.value })} /></Field>
      </div>

      <Field label="Prep time" id="mt" hint="Optional — but it is the field you will use most when picking a Tuesday dinner.">
        <NumberInput id="mt" suffix="min" value={form.prep_min} onChange={(e) => set({ prep_min: e.target.value })} />
      </Field>

      <Field label="Ingredients" id="mi" hint="One per line. The shopping list reads these.">
        <TextArea id="mi" rows={5} value={form.ingredients} onChange={(e) => set({ ingredients: e.target.value })} />
      </Field>

      <Field label="Method" id="mm">
        <TextArea id="mm" rows={3} value={form.method} onChange={(e) => set({ method: e.target.value })} />
      </Field>

      <Field label="Who can eat it" id="mflags">
        <div className="space-y-2">
          <Toggle label="Suits a shared dinner" hint="Gluten-free, lactose-free, no lamb, pork, peppers, cucumber or coconut." checked={form.shared_safe} onChange={(v) => set({ shared_safe: v })} />
          <Toggle label="Gluten-free" checked={form.gluten_free} onChange={(v) => set({ gluten_free: v })} />
          <Toggle label="Lactose-free" checked={form.lactose_free} onChange={(v) => set({ lactose_free: v })} />
          <Toggle label="Favourite" checked={form.favourite} onChange={(v) => set({ favourite: v })} />
        </div>
      </Field>

      <Field label="Tags" id="mtags" hint="Comma separated — quick, batch, weekend, comfort.">
        <TextInput id="mtags" value={form.tags} onChange={(e) => set({ tags: e.target.value })} />
      </Field>

      <Field label="Notes" id="mn">
        <TextArea id="mn" rows={2} value={form.notes} onChange={(e) => set({ notes: e.target.value })} />
      </Field>
    </Sheet>
  );
}

function Toggle({ label, hint, checked, onChange }) {
  return (
    <label className="flex items-start gap-3 p-3 rounded-control border border-edge cursor-pointer">
      <input type="checkbox" checked={checked} onChange={(e) => onChange(e.target.checked)} className="mt-0.5 w-4 h-4 accent-[var(--c-accent)]" />
      <span className="flex-1">
        <span className="text-body-sm font-medium block">{label}</span>
        {hint && <span className="text-meta text-textMute">{hint}</span>}
      </span>
    </label>
  );
}
